import React from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, LockKeyhole, Facebook, Instagram, MapPinned } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { COMPANY, api } from "../lib/site";
import Logo from "./Logo";
import { useLang } from "../i18n/LanguageContext";

export default function Footer() {
  const { t } = useLang();
  const year = new Date().getFullYear();
  const { data: gen } = useQuery({ queryKey: ["settings","general","footer"], queryFn: async () => (await api.get("/settings/general")).data, staleTime: 300000 });
  const s = gen?.value || {};
  const phone = s.phone || COMPANY.phone;
  const phoneClean = "+47" + (phone || "").replace(/\s|\D/g, "");
  const email = s.email || COMPANY.email;
  const area = s.area || COMPANY.area;
  const fb = s.facebook, ig = s.instagram, gb = s.google_business_url;
  return (
    <footer className="bg-[#181B18] text-[#EEE8D9] mt-16" id="site-footer">
      <div id="footer-sentinel" aria-hidden="true" style={{ position: "relative", height: 1 }} />
      <div className="container-page pt-20 pb-14 sm:pt-24 sm:pb-16 grid gap-10 lg:grid-cols-4">
        <div>
          <div className="flex items-center gap-2.5 mb-4">
            <Logo size={42} variant="dark" />
            <div className="text-[#EEE8D9]"><div className="font-display font-extrabold text-lg leading-tight">{gen?.value?.company_name || "NOUROZI"}</div><div className="text-[10px] tracking-[0.18em] text-[#C9C0A8]">{gen?.value?.company_tagline || "EBYGG & ANLEGG"}</div></div>
          </div>
          <p className="text-sm text-[#C9C0A8] leading-relaxed max-w-xs">{t("footer.desc")}</p>
          <div className="mt-5 flex items-center gap-2">
            {fb && <a href={fb} target="_blank" rel="noreferrer noopener" aria-label="Facebook" className="w-9 h-9 rounded-md bg-white/5 hover:bg-[#C49A6C] hover:text-[#0F1210] text-[#EEE8D9] grid place-items-center" data-testid="social-facebook"><Facebook size={16}/></a>}
            {ig && <a href={ig} target="_blank" rel="noreferrer noopener" aria-label="Instagram" className="w-9 h-9 rounded-md bg-white/5 hover:bg-[#C49A6C] hover:text-[#0F1210] text-[#EEE8D9] grid place-items-center" data-testid="social-instagram"><Instagram size={16}/></a>}
            {gb && <a href={gb} target="_blank" rel="noreferrer noopener" aria-label="Google Business" className="w-9 h-9 rounded-md bg-white/5 hover:bg-[#C49A6C] hover:text-[#0F1210] text-[#EEE8D9] grid place-items-center" data-testid="social-google-business"><MapPinned size={16}/></a>}
            {!fb && !ig && !gb && <span className="text-xs text-[#7f7a68]">{t("footer.noSocial")}</span>}
          </div>
        </div>
        <div>
          <h4 className="font-display font-bold text-sm tracking-widest uppercase text-[#C49A6C] mb-4">{t("footer.contact")}</h4>
          <ul className="space-y-3 text-sm">
            <li className="flex items-start gap-2"><Phone size={16} className="mt-0.5 text-[#C49A6C]" /><a href={`tel:${phoneClean}`} className="hover:text-white" data-testid="footer-phone">{phone}</a></li>
            <li className="flex items-start gap-2"><Mail size={16} className="mt-0.5 text-[#C49A6C]" /><a href={`mailto:${email}`} className="hover:text-white break-all" data-testid="footer-email">{email}</a></li>
            <li className="flex items-start gap-2"><MapPin size={16} className="mt-0.5 text-[#C49A6C]" />{area}</li>
          </ul>
        </div>
        <div>
          <h4 className="font-display font-bold text-sm tracking-widest uppercase text-[#C49A6C] mb-4">{t("footer.quicklinks")}</h4>
          <ul className="space-y-2.5 text-sm">
            <li><Link className="hover:text-white" to="/">{t("nav.home")}</Link></li>
            <li><Link className="hover:text-white" to="/tjenester">{t("nav.services")}</Link></li>
            <li><Link className="hover:text-white" to="/prosjekter">{t("nav.projects")}</Link></li>
            <li><Link className="hover:text-white" to="/om-oss">{t("nav.about")}</Link></li>
            <li><Link className="hover:text-white" to="/be-om-pristilbud">{t("nav.quote")}</Link></li>
            <li><Link className="hover:text-white" to="/kontakt">{t("nav.contact")}</Link></li>
            <li><Link className="hover:text-white" to="/personvern">{t("footer.privacyLink")}</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="font-display font-bold text-sm tracking-widest uppercase text-[#C49A6C] mb-4">{t("footer.area")}</h4>
          <p className="text-sm text-[#C9C0A8] leading-relaxed">{t("footer.areaDesc")}</p>
          {gb && <a href={gb} target="_blank" rel="noreferrer noopener" className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-[#C49A6C] hover:text-white" data-testid="footer-google-business"><MapPinned size={12}/> {t("footer.googleBiz")}</a>}
          <Link to="/admin/login" className="mt-6 inline-flex items-center gap-1.5 text-xs text-[#7f7a68] hover:text-[#C9C0A8]" data-testid="footer-admin-link"><LockKeyhole size={12} /> {t("footer.admin")}</Link>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="container-page py-5 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-[#9C9481]">
          <div>© {year} {gen?.value?.company_name ? `${gen.value.company_name} ${gen?.value?.company_tagline || ""}`.trim() : "NOUROZI EBYGG & ANLEGG"}. {t("footer.rights")}</div>
          <div className="flex items-center gap-4"><span>{t("footer.solid")}</span><span>•</span><span>{t("footer.local")}</span><span>•</span><Link className="hover:text-white" to="/personvern">{t("footer.privacyLink")}</Link></div>
        </div>
      </div>
    </footer>
  );
}
